import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Tooltip,
  Badge,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import { BugReport, Close } from '@mui/icons-material';

export interface InlineDevConsoleProps {
  size?: 'small' | 'medium' | 'large';
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
}

const SIZE_CONFIG = {
  small: { iconSize: 18, badgeSize: 12 },
  medium: { iconSize: 20, badgeSize: 14 },
  large: { iconSize: 24, badgeSize: 16 },
};

export const InlineDevConsole: React.FC<InlineDevConsoleProps> = ({
  size = 'medium',
  color = 'inherit',
}) => {
  const [stats, setStats] = useState({
    total: 0,
    errors: 0,
    warnings: 0,
    info: 0,
    logs: 0,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'errors' | 'warnings' | 'info' | 'logs'>(
    'all'
  );

  const { iconSize, badgeSize } = SIZE_CONFIG[size];

  useEffect(() => {
    const updateStats = () => {
      try {
        const globalWindow = window as any;
        if (globalWindow.consoleCapture?.logs?.length > 0) {
          const logs = globalWindow.consoleCapture.logs;

          const newStats = {
            total: logs.length,
            errors: logs.filter((log: any) => log.type === 'ERROR').length,
            warnings: logs.filter((log: any) => log.type === 'WARN').length,
            info: logs.filter((log: any) => log.type === 'INFO').length,
            logs: logs.filter((log: any) => log.type === 'LOG' || log.type === 'DEBUG').length,
          };

          setStats(newStats);

          // Debug logging only when there are discrepancies (comment out for production)
          // if (globalWindow.consoleCapture.originalMethods?.log) {
          //   globalWindow.consoleCapture.originalMethods.log(
          //     'FDC Stats Update:', newStats,
          //     'Total logs in capture:', logs.length,
          //     'Recent logs:', logs.slice(-5).map((l: any) => ({ type: l.type, message: l.message.substring(0, 50) }))
          //   );
          // }
        } else {
          setStats({ total: 0, errors: 0, warnings: 0, info: 0, logs: 0 });
        }
      } catch (error) {
        console.error('Error updating console stats:', error);
      }
    };

    // Update immediately and every 2 seconds
    updateStats();
    const interval = setInterval(updateStats, 2000);

    return () => clearInterval(interval);
  }, []);

  const getFilteredLogs = (filter: string) => {
    try {
      const globalWindow = window as any;
      if (!globalWindow.consoleCapture?.logs) return [];

      const logs = globalWindow.consoleCapture.logs;

      switch (filter) {
        case 'errors':
          return logs.filter((log: any) => log.type === 'ERROR');
        case 'warnings':
          return logs.filter((log: any) => log.type === 'WARN');
        case 'info':
          return logs.filter((log: any) => log.type === 'INFO');
        case 'logs':
          return logs.filter((log: any) => log.type === 'LOG');
        default:
          return logs;
      }
    } catch {
      return [];
    }
  };

  const handleOpenConsole = (filter: 'all' | 'errors' | 'warnings' | 'info' | 'logs' = 'all') => {
    setActiveFilter(filter);
    const filteredLogs = getFilteredLogs(filter);

    const currentUrl = window.location.href;
    const timestamp = new Date().toISOString();

    const header = `=== CONSOLE OUTPUT - ${filter.toUpperCase()} FILTER ===
From: ${currentUrl}
Captured: ${timestamp}
Total messages: ${filteredLogs.length}
=======================================\n\n`;

    const logOutput = filteredLogs
      .map((log: any) => {
        let output = `[${log.timestamp}] ${log.type}: ${log.message}`;

        // Add enhanced details for errors and warnings
        if ((log.type === 'ERROR' || log.type === 'WARN') && log.rawArgs) {
          // Look for error objects in rawArgs
          const errorObj = log.rawArgs.find(
            (arg: any) =>
              arg && (arg.type === 'Error' || arg.type === 'AxiosError' || arg instanceof Error)
          );

          if (errorObj) {
            output += `\n  📍 Error Details:`;
            output += `\n    Type: ${errorObj.type || errorObj.name || 'Error'}`;

            if (errorObj.code) {
              output += `\n    Code: ${errorObj.code}`;
            }

            if (errorObj.status || (errorObj.response && errorObj.response.status)) {
              const status = errorObj.status || errorObj.response.status;
              const statusText =
                errorObj.statusText || (errorObj.response && errorObj.response.statusText);
              output += `\n    HTTP Status: ${status} ${statusText || ''}`;
            }

            if (errorObj.config && errorObj.config.url) {
              output += `\n    Request URL: ${errorObj.config.method?.toUpperCase() || 'GET'} ${errorObj.config.baseURL || ''}${errorObj.config.url}`;
            }

            if (errorObj.response && errorObj.response.data) {
              output += `\n    Response: ${errorObj.response.data}`;
            }

            if (errorObj.fileName) {
              output += `\n    Location: ${errorObj.fileName}:${errorObj.lineNumber}:${errorObj.columnNumber}`;
            }

            if (errorObj.stack) {
              output += `\n    Stack Trace:\n${errorObj.stack}`;
            }
          }

          // Add call stack if available and different from error stack
          if (log.stackTrace && (!errorObj || log.stackTrace !== errorObj.stack)) {
            output += `\n  📞 Call Stack:\n${log.stackTrace}`;
          }

          // Add raw arguments for debugging (but limit size)
          if (log.rawArgs.length > 1) {
            const debugArgs = log.rawArgs.map((arg: any) => {
              if (typeof arg === 'object') {
                // Truncate large objects
                const str = JSON.stringify(arg, null, 2);
                return str.length > 500 ? str.substring(0, 500) + '...[truncated]' : str;
              }
              return arg;
            });
            output += `\n  🔍 Raw Arguments:\n${debugArgs.join('\n')}`;
          }
        }

        return output;
      })
      .join('\n\n================================\n\n');

    setConsoleOutput(header + logOutput);
    setIsOpen(true);
  };

  const handleCopyOutput = async () => {
    try {
      await navigator.clipboard.writeText(consoleOutput);
      const globalWindow = window as any;
      if (globalWindow.showConsoleAlert) {
        globalWindow.showConsoleAlert(
          `Console output copied to clipboard! (${activeFilter} filter)`
        );
      }
    } catch (error) {
      console.error('Failed to copy console output:', error);
    }
  };

  const getTooltipContent = () => {
    if (stats.errors === 0) {
      return 'Console: No errors';
    }

    return `Console: ${stats.errors} error${stats.errors === 1 ? '' : 's'}`;
  };

  const hasIssues = stats.errors > 0;

  return (
    <>
      <Tooltip title={getTooltipContent()} arrow>
        <IconButton
          color={color}
          onClick={() => handleOpenConsole('all')}
          sx={{
            mr: 1,
            opacity: hasIssues ? 1 : 0.5,
          }}
        >
          <Badge
            badgeContent={hasIssues ? stats.errors : null}
            color='error'
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: badgeSize,
                height: badgeSize + 4,
                minWidth: badgeSize + 4,
              },
            }}
          >
            <BugReport sx={{ fontSize: iconSize }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6'>
              Console Output{' '}
              {activeFilter !== 'all' &&
                `- ${activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}`}
            </Typography>
            <IconButton onClick={() => setIsOpen(false)} size='small'>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pb: 1 }}>
          {/* Filter Chips */}
          <Box sx={{ mb: 2 }}>
            <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label={`All (${stats.total})`}
                variant={activeFilter === 'all' ? 'filled' : 'outlined'}
                color={activeFilter === 'all' ? 'primary' : 'default'}
                onClick={() => handleOpenConsole('all')}
                size='small'
              />
              {stats.errors > 0 && (
                <Chip
                  label={`Errors (${stats.errors})`}
                  variant={activeFilter === 'errors' ? 'filled' : 'outlined'}
                  color={activeFilter === 'errors' ? 'error' : 'default'}
                  onClick={() => handleOpenConsole('errors')}
                  size='small'
                />
              )}
              {stats.warnings > 0 && (
                <Chip
                  label={`Warnings (${stats.warnings})`}
                  variant={activeFilter === 'warnings' ? 'filled' : 'outlined'}
                  color={activeFilter === 'warnings' ? 'warning' : 'default'}
                  onClick={() => handleOpenConsole('warnings')}
                  size='small'
                />
              )}
              {stats.info > 0 && (
                <Chip
                  label={`Info (${stats.info})`}
                  variant={activeFilter === 'info' ? 'filled' : 'outlined'}
                  color={activeFilter === 'info' ? 'info' : 'default'}
                  onClick={() => handleOpenConsole('info')}
                  size='small'
                />
              )}
              {stats.logs > 0 && (
                <Chip
                  label={`Logs (${stats.logs})`}
                  variant={activeFilter === 'logs' ? 'filled' : 'outlined'}
                  color={activeFilter === 'logs' ? 'secondary' : 'default'}
                  onClick={() => handleOpenConsole('logs')}
                  size='small'
                />
              )}
            </Stack>
          </Box>

          {/* Console Output */}
          <Paper
            variant='outlined'
            sx={{
              p: 2,
              bgcolor: '#1e1e1e',
              color: '#ffffff',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              maxHeight: '50vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {consoleOutput || 'No console messages to display'}
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCopyOutput} variant='outlined'>
            Copy to Clipboard
          </Button>
          <Button onClick={() => setIsOpen(false)} variant='contained'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
